import { useCallback, useMemo, useState } from "react"
import type { Dispatch, SetStateAction } from "react"

import { type TEmptyCallback } from "@/shared/types/functions"

import { type ICoachingChatSocketClientEvents as IClientEvents } from "../../types"

import { coachingChatSocket as socket } from "./coaching-chat.api.socket"

type _TCallback<TPayload = any> = {
    onEmit?(variables: TPayload): void | Promise<void>
    onSuccess?(variables: TPayload): void
    onError?(error: any, variables: TPayload): void
    onSettled?: TEmptyCallback
}

type _TState = {
    isLoading: boolean
    isSuccess: boolean
    isError: boolean
    error: Error | string | null
}

type _TReturn = _TState & {
    emit: <TPayload>(variables: TPayload, callbacks?: _TCallback<TPayload>) => Promise<void>
    reset: TEmptyCallback
}

function useCoachingChatSocketEmitter<TPayload = any>(
    eventName: keyof IClientEvents
): ReturnType<(eventName: keyof IClientEvents) => _TReturn> {
    const [state, setState]: [_TState, Dispatch<SetStateAction<_TState>>] = useState<_TState>({
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: null
    })

    const emit: <TP = TPayload>(variables: TP, callbacks?: _TCallback<TP>) => Promise<void> = useCallback(
        async <TP>(variables: TP, callbacks?: _TCallback<TP>): Promise<void> => {
            if (!socket.connected) {
                const err = new Error("Socket not connected [emitter]")
                callbacks?.onError?.(err, variables)
                throw err
            }

            setState({
                isLoading: true,
                isSuccess: false,
                isError: false,
                error: null
            })

            await callbacks?.onEmit?.(variables)

            try {
                socket.emit(eventName, variables as never)

                callbacks?.onSuccess?.(variables)

                setState({
                    isLoading: false,
                    isSuccess: true,
                    isError: false,
                    error: null
                })
            } catch (err) {
                setState({
                    isLoading: false,
                    isSuccess: false,
                    isError: true,
                    error: err instanceof Error ? err : String(err)
                })

                callbacks?.onError?.(err, variables)

                throw err
            } finally {
                callbacks?.onSettled?.()
            }
        },
        [eventName]
    )

    const reset: TEmptyCallback = useCallback(
        (): void =>
            setState({
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: null
            }),
        []
    )

    return useMemo(
        (): _TReturn => ({
            ...state,
            emit,
            reset
        }),
        [state, emit, reset]
    )
}

export { useCoachingChatSocketEmitter }
